import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "../../../../../nodes/websiteBuilderNode";
import {
  getElementPosition,
  IWebsiteBuilderElementStretch,
  TWebsiteBuilderElementType,
} from "../../../../../nodes/element/websiteBuilderElement";
import { TWebsiteBuilderTreeRefMap } from "../../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import {
  TElementViewResizeHandlePointerDown,
  TElementViewResizeHandle,
  ElementViewResizeHandle,
} from "./ElementViewResizeHandle";
import { useAppDispatch } from "hooks/redux";
import {
  websiteBuilderResizeElement,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { clamp } from "lodash";
import { WEBSITE_BUILDER_DESIGN_PAGE_WIDTH } from "../../../../WebsiteBuilderDesignSpace";
import { WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY } from "../../../../../nodes/section/websiteBuilderSection";
import { ElementViewIndicator, TElementViewIndicator } from "../helpers/ElementViewIndicator";
import { IWebsiteBuilderDesignSpaceBoundaries } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";

export interface IUseElementViewResizeArgs {
  id: string;
  parentId: string;
  type: TWebsiteBuilderElementType;
  position: IWebsiteBuilderNodePosition;
  screenPosition: IWebsiteBuilderNodePosition;
  dimensions: IWebsiteBuilderNodeDimensions;
  screenDimensions: IWebsiteBuilderNodeDimensions;
  selected: boolean;
  resizing: boolean;
  designSpaceBoundaries: IWebsiteBuilderDesignSpaceBoundaries;
  stretch: IWebsiteBuilderElementStretch;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

interface IElementViewResizeRefState {
  pointerId: number;
  currentTarget: Element;
  prevPosition: IWebsiteBuilderNodePosition;
  nextPosition: IWebsiteBuilderNodePosition;
  prevDimensions: IWebsiteBuilderNodeDimensions;
  nextDimensions: IWebsiteBuilderNodeDimensions;
  resizingAllowed: boolean;
  resizing: boolean;
}

interface IElementViewResizeState {
  handle: TElementViewResizeHandle;
  screenDimensions: IWebsiteBuilderNodeDimensions;
  screenPosition: IWebsiteBuilderNodePosition;
}

interface IUseElementViewResize {
  viewResizeScreenPosition: IWebsiteBuilderNodePosition;
  viewResizeScreenDimensions: IWebsiteBuilderNodeDimensions;
  viewResizeHandles: JSX.Element;
  viewResizeIndicators: JSX.Element;
}

const allHandlesCategory: TElementViewResizeHandle[] = [
  "NorthWest",
  "North",
  "NorthEast",
  "West",
  "East",
  "SouthWest",
  "South",
  "SouthEast",
];

const lateralHandlesCategory: TElementViewResizeHandle[] = ["West", "East"];

const southOrLateralHandlesCategory: TElementViewResizeHandle[] = ["South", "West", "East"];

const RESIZE_MINIMUM_WIDTH = 25;
const RESIZE_MINIMUM_HEIGHT = 25;

export function useElementViewResize(args: IUseElementViewResizeArgs): IUseElementViewResize {
  const dispatch = useAppDispatch();

  const resizeRefState = useRef<IElementViewResizeRefState>({
    pointerId: null,
    currentTarget: null,
    prevPosition: null,
    nextPosition: null,
    prevDimensions: null,
    nextDimensions: null,
    resizingAllowed: false,
    resizing: false,
  });

  const [resizeState, setResizeState] = useState<IElementViewResizeState>({
    handle: null,
    screenDimensions: null,
    screenPosition: null,
  });

  const indicator: TElementViewIndicator = useMemo(() => {
    if (args.resizing) {
      return {
        type: "dimensions",
        position: resizeState.screenPosition,
        dimensions: resizeState.screenDimensions,
      };
    } else {
      return null;
    }
  }, [args.resizing, resizeState.screenPosition, resizeState.screenDimensions]);

  const viewResizeScreenDimensions: IWebsiteBuilderNodeDimensions = useMemo(() => {
    if (args.resizing && resizeState.screenDimensions) {
      return resizeState.screenDimensions;
    } else {
      return args.screenDimensions;
    }
  }, [args.resizing, resizeState.screenDimensions, args.screenDimensions]);

  const viewResizeScreenPosition: IWebsiteBuilderNodePosition = useMemo(() => {
    if (args.resizing && resizeState.screenPosition) {
      return resizeState.screenPosition;
    } else {
      return args.screenPosition;
    }
  }, [args.resizing, resizeState.screenPosition, args.screenPosition]);

  function resetStates() {
    resizeRefState.current = {
      pointerId: null,
      currentTarget: null,
      prevPosition: null,
      nextPosition: null,
      prevDimensions: null,
      nextDimensions: null,
      resizingAllowed: false,
      resizing: false,
    };

    setResizeState({
      handle: null,
      screenPosition: null,
      screenDimensions: null,
    });
  }

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      resizeRefState.current.resizingAllowed = false;

      const { pointerId, currentTarget } = resizeRefState.current;

      if (pointerId != null) {
        currentTarget?.releasePointerCapture(pointerId);
      }

      resetStates();

      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            editingMode: "none",
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (args.resizing) {
      document.addEventListener("keydown", handleKeydown);
    }

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [args.resizing, handleKeydown]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { resizingAllowed, resizing } = resizeRefState.current;

      if (!resizingAllowed) {
        return;
      }

      const ref = args.treeRefMap.current.get(args.id);
      const parentRef = args.treeRefMap.current.get(args.parentId);

      const boundingRect = ref?.getBoundingClientRect();
      const parentBoundingRect = parentRef?.getBoundingClientRect();

      if (!boundingRect || !parentBoundingRect) {
        return;
      }

      const nextScreenPosition: IWebsiteBuilderNodePosition = {
        x: boundingRect.left - parentBoundingRect.left,
        y: boundingRect.top - parentBoundingRect.top,
      };

      const nextScreenDimensions: IWebsiteBuilderNodeDimensions = {
        width: boundingRect.width,
        height: boundingRect.height,
      };

      if (resizeState.handle === "SouthWest" || resizeState.handle === "West" || resizeState.handle === "NorthWest") {
        const xDelta = e.clientX - boundingRect.left;

        const x = nextScreenPosition.x + xDelta;

        const leftBoundary = args.designSpaceBoundaries.leftX + SECTION_CHILD_PLACEMENT_BOUNDARY;

        const right = boundingRect.right - parentBoundingRect.left;
        const rightBoundary = right - RESIZE_MINIMUM_WIDTH;

        if (x < leftBoundary) {
          nextScreenPosition.x = leftBoundary;
          nextScreenDimensions.width = right - leftBoundary;
        } else if (x > rightBoundary) {
          nextScreenPosition.x = rightBoundary;
          nextScreenDimensions.width = RESIZE_MINIMUM_WIDTH;
        } else {
          nextScreenPosition.x = x;
          nextScreenDimensions.width -= xDelta;
        }
      }

      if (resizeState.handle === "NorthWest" || resizeState.handle === "North" || resizeState.handle === "NorthEast") {
        const yDelta = e.clientY - boundingRect.top;

        const y = nextScreenPosition.y + yDelta;

        const bottom = boundingRect.bottom - parentBoundingRect.top;
        const bottomBoundary = bottom - RESIZE_MINIMUM_HEIGHT;

        if (y < SECTION_CHILD_PLACEMENT_BOUNDARY) {
          nextScreenPosition.y = SECTION_CHILD_PLACEMENT_BOUNDARY;
          nextScreenDimensions.height = bottom - SECTION_CHILD_PLACEMENT_BOUNDARY;
        } else if (y > bottomBoundary) {
          nextScreenPosition.y = bottomBoundary;
          nextScreenDimensions.height = RESIZE_MINIMUM_HEIGHT;
        } else {
          nextScreenPosition.y = y;
          nextScreenDimensions.height -= yDelta;
        }
      }

      if (resizeState.handle === "NorthEast" || resizeState.handle === "East" || resizeState.handle === "SouthEast") {
        const updatedWidth = e.clientX - boundingRect.left;
        const maxWidth =
          parentBoundingRect.left +
          args.designSpaceBoundaries.rightX -
          boundingRect.left -
          SECTION_CHILD_PLACEMENT_BOUNDARY;

        nextScreenDimensions.width = clamp(updatedWidth, RESIZE_MINIMUM_WIDTH, maxWidth);
      }

      if (resizeState.handle === "SouthEast" || resizeState.handle === "South" || resizeState.handle === "SouthWest") {
        const updatedHeight = e.clientY - boundingRect.top;
        const maxHeight = parentBoundingRect.bottom - boundingRect.top - SECTION_CHILD_PLACEMENT_BOUNDARY;

        nextScreenDimensions.height = clamp(updatedHeight, RESIZE_MINIMUM_HEIGHT, maxHeight);
      }

      const nextPosition = getElementPosition(args.designSpaceBoundaries.leftX, nextScreenPosition);
      const nextDimensions: IWebsiteBuilderNodeDimensions = { ...nextScreenDimensions };

      if (args.stretch?.enabled) {
        nextScreenPosition.x = args.stretch.margin;
        nextScreenDimensions.width =
          args.designSpaceBoundaries.leftX + args.designSpaceBoundaries.rightX - args.stretch.margin * 2;

        nextPosition.x = SECTION_CHILD_PLACEMENT_BOUNDARY;
        nextDimensions.width = WEBSITE_BUILDER_DESIGN_PAGE_WIDTH - SECTION_CHILD_PLACEMENT_BOUNDARY * 2;
      }

      resizeRefState.current.nextPosition = nextPosition;
      resizeRefState.current.nextDimensions = nextDimensions;

      setResizeState(prev => ({
        ...prev,
        screenPosition: nextScreenPosition,
        screenDimensions: nextScreenDimensions,
      }));

      if (!resizing) {
        resizeRefState.current.resizing = true;

        dispatch(
          websiteBuilderUpdateSelectedNode({
            selectedNodeUpdates: {
              editingMode: "element-resize",
            },
          }),
        );
      }
    },
    [
      args.id,
      args.parentId,
      resizeState.handle,
      args.designSpaceBoundaries.leftX,
      args.designSpaceBoundaries.rightX,
      args.stretch,
    ],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { resizingAllowed, resizing, prevPosition, nextPosition, prevDimensions, nextDimensions, currentTarget } =
        resizeRefState.current;

      if (!resizingAllowed) {
        return;
      }

      if (resizing) {
        if (
          prevPosition &&
          nextPosition &&
          prevDimensions &&
          nextDimensions &&
          (Math.abs(prevDimensions.width - nextDimensions.width) > 1 ||
            Math.abs(prevDimensions.height - nextDimensions.height) > 1)
        ) {
          dispatch(
            websiteBuilderResizeElement({
              id: args.id,
              prevPosition: { ...prevPosition },
              nextPosition: { ...nextPosition },
              prevDimensions: { ...prevDimensions },
              nextDimensions: { ...nextDimensions },
            }),
          );
        } else {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                editingMode: "none",
              },
            }),
          );
        }
      }

      currentTarget?.releasePointerCapture(e.pointerId);

      resetStates();
    },
    [args.designSpaceBoundaries.leftX, args.id],
  );

  useEffect(() => {
    if (resizeState.handle) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizeState.handle, handlePointerMove, handlePointerUp]);

  const onPointerDown: TElementViewResizeHandlePointerDown = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.button !== 0) {
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);

    resizeRefState.current.pointerId = e.pointerId;
    resizeRefState.current.currentTarget = e.currentTarget;

    resizeRefState.current.prevPosition = { ...args.position };
    resizeRefState.current.nextPosition = { ...args.position };

    resizeRefState.current.prevDimensions = { ...args.dimensions };
    resizeRefState.current.nextDimensions = { ...args.dimensions };

    resizeRefState.current.resizing = false;
    resizeRefState.current.resizingAllowed = true;

    setResizeState(prev => ({
      ...prev,
      handle,
    }));
  };

  function renderResizeHandleCategory(category: TElementViewResizeHandle[]) {
    return (
      <>
        {category.map(handle => {
          if (!args.stretch?.enabled || handle === "North" || handle === "South") {
            return (
              <ElementViewResizeHandle
                key={handle}
                handle={handle}
                viewWidth={viewResizeScreenDimensions.width}
                viewHeight={viewResizeScreenDimensions.height}
                onPointerDown={onPointerDown}
              />
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  function renderResizeHandles() {
    if (!args.selected) {
      return null;
    }

    if (args.type === "button" || args.type === "input" || args.type === "select") {
      return renderResizeHandleCategory(allHandlesCategory);
    } else if (args.type === "divider") {
      return renderResizeHandleCategory(lateralHandlesCategory);
    } else if (args.type === "paragraph" || args.type === "heading") {
      return renderResizeHandleCategory(southOrLateralHandlesCategory);
    } else {
      return null;
    }
  }

  return {
    viewResizeScreenPosition,
    viewResizeScreenDimensions,
    viewResizeHandles: renderResizeHandles(),
    viewResizeIndicators: <ElementViewIndicator indicator={indicator} />,
  };
}
